import {Observable} from 'rxjs';
import {
    CpmCServiceRequestGroupCreateApiSchema,
    CpmCServiceRequestGroupUpdateApiSchema
} from '../../schemas/c-customer/cpmc-service-request-group.api-schemas';
import {CpmCApiBridge} from '../../../../../src/app/cpmc-api-bridge';
import {CpmCQueryPaginatorConfig, CpmCQueryPaginatorConfigUtils} from '../../cpmc-defines';
import {CpmCServiceRequestGroupApiModel} from '../../models/c-customer/cpmc-service-request-group.api-model';
import {AllowedQueryParamTypes, QueryParams} from '../../../../../sdk/src/utils/query.utils';
import {CpmCPaginatedResultAPISchema} from '../../schemas/cpmc-paginated-result.api-schemas';

export type CpmCGetServiceRequestGroupsParams = {
    [K in keyof CpmCServiceRequestGroupApiModel]?: CpmCServiceRequestGroupApiModel[K] extends AllowedQueryParamTypes
        ? CpmCServiceRequestGroupApiModel[K]
        : never;
} & {} & QueryParams;

/**
 * ServiceRequestGroup Api Service
 */
export class CpmCServiceRequestGroupApiService {
    public static readonly dPaginatorConfig: CpmCQueryPaginatorConfig = {limit: -1, sort: 'name', order: 1};

    // ===============================================================
    // ===============================================================

    public static baseUrl(inCustomerUUID: string | undefined) {
        const base = CpmCApiBridge.getBackendBaseURL() + 'api/core/v1/';
        const customerUUID = CpmCApiBridge.getCurrentCustomerUUID();

        return base + `customer/${customerUUID || inCustomerUUID}/service-request-group`;
    }

    // ===============================================================
    // ===============================================================

    public static endpointList(inCustomerUUID: string | undefined): string {
        return `${CpmCServiceRequestGroupApiService.baseUrl(inCustomerUUID)}/list`;
    }
    public static endpointNew(inCustomerUUID: string | undefined): string {
        return `${CpmCServiceRequestGroupApiService.baseUrl(inCustomerUUID)}`;
    }
    public static endpointGet(inCustomerUUID: string | undefined, serviceRequestGroupUUID: string): string {
        return `${CpmCServiceRequestGroupApiService.baseUrl(inCustomerUUID)}/${serviceRequestGroupUUID}`;
    }
    public static endpointEdit(inCustomerUUID: string | undefined, serviceRequestGroupUUID: string): string {
        return `${CpmCServiceRequestGroupApiService.baseUrl(inCustomerUUID)}/${serviceRequestGroupUUID}`;
    }
    public static endpointDelete(inCustomerUUID: string | undefined, serviceRequestGroupUUID: string): string {
        return `${CpmCServiceRequestGroupApiService.baseUrl(inCustomerUUID)}/${serviceRequestGroupUUID}`;
    }

    // ===============================================================
    // ===============================================================

    public static getServiceRequestGroupList(
        params: CpmCGetServiceRequestGroupsParams = {},
        paginatorConfig: CpmCQueryPaginatorConfig = CpmCServiceRequestGroupApiService.dPaginatorConfig
    ): Observable<CpmCPaginatedResultAPISchema<CpmCServiceRequestGroupApiModel>> {
        return CpmCApiBridge.httpGet<CpmCPaginatedResultAPISchema<CpmCServiceRequestGroupApiModel>>(
            CpmCServiceRequestGroupApiService.endpointList(undefined),
            {
                ...(params as QueryParams),
                ...CpmCQueryPaginatorConfigUtils.toQueryParams(paginatorConfig)
            }
        );
    }
    public static getServiceRequestGroupByUUID(
        serviceRequestGroupUUID: string
    ): Observable<CpmCServiceRequestGroupApiModel> {
        return CpmCApiBridge.httpGet<CpmCServiceRequestGroupApiModel>(
            CpmCServiceRequestGroupApiService.endpointGet(undefined, serviceRequestGroupUUID)
        );
    }
    public static createServiceRequestGroup(
        payload: CpmCServiceRequestGroupCreateApiSchema
    ): Observable<CpmCServiceRequestGroupApiModel> {
        return CpmCApiBridge.httpPost<CpmCServiceRequestGroupApiModel>(
            CpmCServiceRequestGroupApiService.endpointNew(undefined),
            payload
        );
    }
    public static updateServiceRequestGroup(
        serviceRequestGroupUUID: string,
        payload: CpmCServiceRequestGroupUpdateApiSchema
    ): Observable<CpmCServiceRequestGroupApiModel> {
        return CpmCApiBridge.httpPut<CpmCServiceRequestGroupApiModel>(
            CpmCServiceRequestGroupApiService.endpointEdit(undefined, serviceRequestGroupUUID),
            payload
        );
    }
    public static deleteServiceRequestGroup(serviceRequestGroupUUID: string): Observable<boolean> {
        return CpmCApiBridge.httpDelete<boolean>(
            CpmCServiceRequestGroupApiService.endpointDelete(undefined, serviceRequestGroupUUID)
        );
    }
}
